import { Injectable } from '@angular/core';
import {
	HttpContext,
	HttpContextToken,
	HttpErrorResponse,
	HttpEvent,
	HttpHandler,
	HttpInterceptor,
	HttpRequest,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { ActivatedRoute, Event as NavigationEvent, NavigationStart, Router } from '@angular/router';
import { UtilService } from '@service/util/util.service';
import { DATE_TIME, IS_EMPTY, IS_NOT_EMPTY, IS_NOT_NULL_OR_UNDEFINED } from 'cargo-chief-commons';

import { environment } from '@environment/environment';
import { API_PATH, CARGOCHIEF_KEYS } from '@domain/constants/app-variables';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { API_ERROR_STRING, UNABLE_TO_PROCESS_REQUEST } from '@domain/constants/error';
import { HttpCancelService } from '../http-cancel/http-cancel.service';
import { TokenStoreService } from '../auth/token-store.service';
import { TokenRefreshService } from '../auth/token-refresh.service';
import { catchError, filter, map, switchMap, takeUntil } from 'rxjs/operators';

/**
 * CN-8597 Phase 3 — marks a request as a replay of a previously-401'd call
 * AFTER a successful token refresh. Used by handleRequest() to suppress the
 * 401-triggered refresh path on the replay itself; without this guard a
 * replay that immediately 401'd would trigger another refresh and recurse.
 */
const AUTH_REPLAY = new HttpContextToken<boolean>(() => false);

@Injectable({
	providedIn: 'root',
})
export class InterceptorService implements HttpInterceptor {
	private LOGIN_SETUP_URL = 'loginSetup';

	constructor(
		private router: Router,
		private utilService: UtilService,
		private _activeRoute: ActivatedRoute,
		private httpCancelService: HttpCancelService,
		// CN-8597 Phase 3 — Bearer-token attach and 401-refresh-replay
		// gated on environment.USE_HECATE_LOGIN. No-op when the legacy
		// cookie flow is still active.
		private readonly tokenStore: TokenStoreService,
		private readonly tokenRefresh: TokenRefreshService
	) {
		router.events
			.pipe(
				filter((event: NavigationEvent) => {
					return event instanceof NavigationStart;
				})
			)
			.subscribe((event: NavigationStart) => {
				const currentRoute = this.router.url.split('?')[0];
				const targetRoute = event.url.split('?')[0];
				if (currentRoute !== targetRoute) {
					this.httpCancelService.cancelPendingRequests();
				}
			});
	}

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		// CN-8597 Phase 3 — proactive refresh. If the stored access token is
		// within its leeway window of expiry, refresh BEFORE sending so the
		// request goes out with a fresh token instead of having to recover
		// from a 401 mid-flight. Skipped for the /oauth/token endpoint itself
		// (we don't want to recursively refresh while refreshing).
		if (this.shouldProactivelyRefresh(req)) {
			return this.tokenRefresh.refresh().pipe(
				switchMap((newAccessToken) =>
					this.handleRequest(this.cloneWithBearer(req, newAccessToken), next)
				),
				// Refresh failed — fall through and try with the (possibly
				// stale) token we already have. If the server then 401s, the
				// catchError block below will get its turn.
				catchError(() => this.handleRequest(this.attachBearerIfApplicable(req), next))
			);
		}

		return this.handleRequest(this.attachBearerIfApplicable(req), next);
	}

	/**
	 * Common request-handler tail: cancel-on-navigation + the loginSetup
	 * timestamp side-effect + the 401/500/502/503 catchError. Extracted so the
	 * proactive-refresh path above and the normal path share one implementation.
	 */
	private handleRequest(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		return next
			.handle(req)
			.pipe(takeUntil(this.httpCancelService.onCancelPendingRequests(req)))
			.pipe(
				map((event: HttpEvent<any>) => {
					if (req?.url?.indexOf(this.LOGIN_SETUP_URL) > -1) {
						localStorage.setItem(CARGOCHIEF_KEYS.LOGIN_SETUP_LAST_UPDATE, DATE_TIME(new Date()));
					}
					return event;
				})
			)
			.pipe(
				catchError((err: any) => {
					if (err instanceof HttpErrorResponse) {
						if (err.status === 401) {
							// CN-8597 Phase 3 — try a refresh-and-replay before
							// bouncing the user. TokenRefreshService is shared
							// across concurrent 401s (queue-and-replay) so a
							// burst of failed requests only triggers one
							// /oauth/token call. The replay is marked with
							// AUTH_REPLAY so this branch suppresses itself
							// when handleRequest is re-entered for the retry;
							// otherwise a 401 on the replay would recurse
							// into another refresh.
							if (
								environment.USE_HECATE_LOGIN &&
								this.shouldAttemptRefresh(req) &&
								!this.isReplay(req)
							) {
								return this.tokenRefresh.refresh().pipe(
									// 1) Refresh itself failed → session expired.
									catchError(() => this.handleSessionExpired(err)),
									// 2) Refresh succeeded → replay through the
									//    same handleRequest tail (takeUntil +
									//    loginSetup side-effect + 500/502/503
									//    handling). The AUTH_REPLAY flag gates
									//    out this branch on the retry, so a
									//    401-on-replay short-circuits to
									//    handleSessionExpired below.
									switchMap((newAccessToken) =>
										this.handleRequest(this.cloneAsReplay(req, newAccessToken), next)
									)
								);
							}
							// AUTH_REPLAY-marked 401 means the freshly-refreshed
							// token was rejected immediately — treat as session
							// expired.
							if (environment.USE_HECATE_LOGIN && this.isReplay(req)) {
								return this.handleSessionExpired(err);
							}
							// USE_HECATE_LOGIN on but request was NOT c4 (e.g.
							// a 401 from a third-party host or from
							// /oauth/token itself on bad creds) — don't bounce
							// the user to /login; just let the error surface
							// so the calling component can display it.
							if (environment.USE_HECATE_LOGIN && !this.isC4Request(req)) {
								return throwError(err);
							}
							return this.handleSessionExpired(err);
						} else if (err.status === 500) {
							const errorId = err.error?.errorId || 'unknown';
							this.utilService.showSnackbar(
								`An unexpected error occurred. Please reference this error ID: ${errorId}`,
								SNACKBAR.ERROR,
								SNACKBAR.ACTION
							);
						} else if (err.status === 502) {
							this.utilService.showSnackbar(
								API_ERROR_STRING.ERR_502,
								SNACKBAR.WARNING,
								SNACKBAR.ACTION
							);
						} else if (err.status === 503) {
							this.router.navigate(['maintenance']);
						}
						let customError: any = JSON.parse(JSON.stringify(err));
						let key = this.isHTMlError(customError);
						if (IS_NOT_NULL_OR_UNDEFINED(key)) {
							customError[key] = UNABLE_TO_PROCESS_REQUEST;
						}
						return throwError(customError);
					}
				})
			);
	}

	private isHTMlError(customError: any) {
		let error_keys = ['data', 'error', 'error'['text'], 'error'['message']];
		for (var key in error_keys) {
			if (
				IS_NOT_EMPTY(customError[error_keys[key]]) &&
				customError[error_keys[key]].indexOf('DOCTYPE') !== -1
			) {
				return error_keys[key];
			}
		}
		return null;
	}

	/**
	 * CN-8597 Phase 3 — clone the request with an Authorization: Bearer
	 * header IF this is a c4-targeted request and we have an access token.
	 * Skip for the /oauth/token endpoint itself (unauthenticated public
	 * client) and any other off-host call (don't leak the JWT outside c4).
	 */
	private attachBearerIfApplicable(req: HttpRequest<any>): HttpRequest<any> {
		if (!environment.USE_HECATE_LOGIN) {
			return req;
		}
		if (!this.isC4Request(req)) {
			return req;
		}
		const token = this.tokenStore.getAccessToken();
		if (!token) {
			return req;
		}
		return this.cloneWithBearer(req, token);
	}

	private cloneWithBearer(req: HttpRequest<any>, token: string): HttpRequest<any> {
		return req.clone({
			setHeaders: { Authorization: `Bearer ${token}` },
		});
	}

	/**
	 * CN-8597 Phase 3 — clone the request with a fresh Authorization header AND
	 * mark it as a refresh-replay via {@link AUTH_REPLAY}. The context flag is
	 * client-side only (not sent over the wire) so it doesn't affect CORS or
	 * the backend; handleRequest reads it to suppress the 401-refresh branch
	 * on this specific retry.
	 */
	private cloneAsReplay(req: HttpRequest<any>, token: string): HttpRequest<any> {
		return req.clone({
			setHeaders: { Authorization: `Bearer ${token}` },
			context: req.context.set(AUTH_REPLAY, true),
		});
	}

	private isReplay(req: HttpRequest<any>): boolean {
		return req.context.get(AUTH_REPLAY) === true;
	}

	/**
	 * A request targets c4 when its URL begins with environment.apiBaseUrl OR
	 * is a relative URL (RestService sometimes builds relative paths for
	 * proxied calls). Off-host third-party calls and the /oauth/token endpoint
	 * itself are skipped.
	 */
	private isC4Request(req: HttpRequest<any>): boolean {
		const url = req?.url ?? '';
		if (!url) {
			return false;
		}
		// Exclude hecate's /oauth/token explicitly.
		if (environment.AUTH_SERVER_URL && url.startsWith(environment.AUTH_SERVER_URL)) {
			return false;
		}
		// Same-host (relative URLs or apiBaseUrl-prefixed) → c4.
		const apiBase = environment.apiBaseUrl ?? '';
		if (apiBase && url.startsWith(apiBase)) {
			return true;
		}
		// Relative paths starting with '/' are also c4-served.
		return url.startsWith('/');
	}

	/**
	 * CN-8597 Phase 3 — only attempt a refresh when we have a refresh token
	 * AND the 401 came from a c4 endpoint. A 401 from anywhere else (e.g.
	 * /oauth/token itself with bad credentials) should not trigger a refresh
	 * loop.
	 */
	private shouldAttemptRefresh(req: HttpRequest<any>): boolean {
		return (
			this.isC4Request(req) &&
			this.tokenStore.getRefreshToken() !== null
		);
	}

	/**
	 * CN-8597 Phase 3 — should we refresh BEFORE sending? Only when the
	 * hecate flow is on, the request targets c4, we have both tokens, and the
	 * access token is within the leeway window of expiry. Avoids the
	 * 401-then-retry round trip for predictable expiries.
	 */
	private shouldProactivelyRefresh(req: HttpRequest<any>): boolean {
		return (
			environment.USE_HECATE_LOGIN &&
			this.isC4Request(req) &&
			this.tokenStore.hasAccessToken() &&
			this.tokenStore.getRefreshToken() !== null &&
			this.tokenStore.isExpiringSoon()
		);
	}

	/**
	 * Legacy session-expired handling — extracted from the original 401
	 * branch so the Phase 3 refresh-and-replay path can fall through to it
	 * when a refresh fails. Behavior unchanged from pre-CN-8597.
	 */
	private handleSessionExpired(err: HttpErrorResponse): Observable<never> {
		localStorage.clear();
		let errorMessage = err['error']?.['message'];
		if (IS_EMPTY(errorMessage)) {
			errorMessage = err.error;
		}
		localStorage.setItem('sessionExpired', 'true');
		if (IS_NOT_EMPTY(errorMessage) && typeof errorMessage === 'string' && errorMessage.indexOf('DOCTYPE') == -1) {
			localStorage.setItem('sessionErrorMsg', errorMessage);
		}
		this.router.navigate(['login']);
		const customError: any = JSON.parse(JSON.stringify(err));
		const key = this.isHTMlError(customError);
		if (IS_NOT_NULL_OR_UNDEFINED(key)) {
			customError[key] = UNABLE_TO_PROCESS_REQUEST;
		}
		return throwError(customError);
	}
}
